import SectionHeader from "@/components/site/SectionHeader";
import PackageCard from "@/components/site/PackageCard";
import { HOSTING_PLANS } from "@/lib/packages";
import { Wrench, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema } from "@/components/seo/schema";

export default function Hosting() {
  return (
    <div data-testid="page-hosting">
      <Seo
        title="Website Hosting & Maintenance from $29/mo | Equinox Systems"
        description="Secure, fast website hosting and maintenance from $29/month — daily backups, SSL, security monitoring, and discounted maintenance for hosting clients with free maintenance on Premium. Included free in our Free Website Program."
        path="/hosting"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Hosting", path: "/hosting" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            as="h1"
            eyebrow="Hosting & Maintenance"
            title="Reliable hosting. Predictable maintenance."
            description="Fast, secure hosting with daily backups, plus optional maintenance work to keep your site healthy and up-to-date."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOSTING_PLANS.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>

          <div className="mt-20 grid lg:grid-cols-2 gap-6">
            <div className="rounded-3xl p-10 glass">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/15 border border-blue-500/30 grid place-items-center text-blue-300">
                <Wrench className="w-6 h-6" strokeWidth={1.7} />
              </div>
              <h3 className="mt-5 font-display text-2xl md:text-3xl font-semibold">
                Maintenance & Updates
              </h3>
              <p className="mt-3 text-gray-400 leading-relaxed">
                Standard maintenance is billed at <span className="text-white">$50/hour</span> depending
                on scope and complexity. Hosting clients receive discounted maintenance rates based on
                their hosting plan, with Premium Hosting including maintenance at no additional cost.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-gray-300">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Standard maintenance: $50/hour</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Basic Hosting clients: $40/hour</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Business Hosting clients: $30/hour</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Premium Hosting clients: Maintenance included at no additional cost</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Pay only for the time you use (Basic &amp; Business plans)</li>
              </ul>
              <Link to="/quote" className="btn-primary mt-8" data-testid="hosting-quote-btn">
                Request Maintenance Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="rounded-3xl p-10 glass">
              <div className="text-xs uppercase tracking-widest text-blue-400">Why our hosting</div>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold">
                Hosted for performance, monitored for peace of mind.
              </h3>
              <ul className="mt-6 space-y-3 text-sm text-gray-300">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Daily backups + restore points</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> SSL certificate provisioning</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Security monitoring + alerts</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Priority support on higher tiers</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Stripe-secure monthly payments</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
